import { useEffect, useState } from "react";
import {
  Modal,
  Upload,
  Button,
  message,
  Steps,
  Form,
  Input,
  DatePicker,
  Select,
  Radio,
  Table,
  Card,
  Space,
  Typography,
  Alert,
  UploadFile,
  Row,
  Col,
  Tooltip,
} from "antd";
import type { RcFile } from "antd/es/upload";
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  DeleteOutlined,
  CalendarOutlined,
  SearchOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import saveAs from "file-saver";
import * as ExcelJS from "exceljs";
import "./index.css";
import { postApi, getApi, postWithParamsApi } from "redux/apis";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { getWarrantyDetailsActionTable } from "redux/action/productWarrantyDetailsAction";
import { toastText } from "utils/utils";
import dayjs from "dayjs";

const { Text } = Typography;

interface UploadInvoiceModalProps {
  setIsModalVisible: (visible: boolean) => void;
  isModalVisible: boolean;
  searchParams: { fields: string[]; value: string };
  pageSize: number;
  setIsLoading: (visible: boolean) => void;
  currentPage: number;
}

type UploadMode = "serial-only" | "item-serial";

interface PreviewRow {
  rowNumber: number;
  item: string;
  serial: string;
  status: "valid" | "invalid" | "duplicate";
  error?: string;
  errorColumn?: string;
}

const UploadInvoiceModal = ({
  isModalVisible,
  setIsModalVisible,
  setIsLoading,
  searchParams,
  pageSize,
  currentPage,
}: UploadInvoiceModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  
  // Watch form values to enable/disable Next button
  const customerName = Form.useWatch("customerName", form);
  const invoiceNumber = Form.useWatch("invoiceNumber", form);
  const invoiceDate = Form.useWatch("invoiceDate", form);
  
  // Check if all required fields are filled
  const isStep0Complete = !!(customerName && invoiceNumber && invoiceDate);
  
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadMode, setUploadMode] = useState<UploadMode>("serial-only");
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Array<{ value: string; label: string; id: number }>>([]);
  const [items, setItems] = useState<Array<{ value: string; label: string; id: number }>>([]);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [validationSummary, setValidationSummary] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0,
  });
  const [previewPagination, setPreviewPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [formValues, setFormValues] = useState<{
    customerName?: string;
    invoiceNumber?: string;
    invoiceDate?: dayjs.Dayjs | null;
  }>({});
  const [importResults, setImportResults] = useState<{
    successful: number;
    failed: number;
    errorData: Array<{ rowNumber: number; serial: string; error: string; column?: string }>;
  }>({
    successful: 0,
    failed: 0,
    errorData: [],
  });
  const dispatch = useDispatch<AppDispatch>();

  // Fetch customers and items on mount
  useEffect(() => {
    if (isModalVisible) {
      fetchCustomers();
      fetchItems();
    }
  }, [isModalVisible]);

  const fetchCustomers = async () => {
    try {
      const response = await getApi("/sales-order/customerName");
      if (response.status === 200) {
        setCustomers(
          response.data.data.map((customer: { name: string; id: number }) => ({
            value: customer.name,
            label: customer.name,
            id: customer.id,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const fetchItems = async () => {
    try {
      // Fetch all items by making multiple calls if needed
      let allItems: Array<{ value: string; label: string; id: number }> = [];
      let pageNumber = 1;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const params = {
          pageNumber: pageNumber,
          pageSize: pageSize,
        };
        const body = {
          fields: [],
          searchValue: "",
        };

        const response = await postWithParamsApi("/items/getItems", body, params);
        
        if (response.status === 200 && response.data?.data?.items) {
          const items = response.data.data.items.map((item: { name: string; id: number }) => ({
            value: item.name,
            label: item.name,
            id: item.id,
          }));
          
          allItems = [...allItems, ...items];
          
          const totalRecords = response.data.data.totalRecords || 0;
          hasMore = allItems.length < totalRecords;
          pageNumber++;
        } else {
          hasMore = false;
        }
      }

      setItems(allItems);
    } catch (error) {
      console.error("Failed to fetch items:", error);
    }
  };

  const downloadSampleFile = async (mode: UploadMode) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("SampleData");

    let data: (string | number)[][];
    if (mode === "serial-only") {
      data = [["Serial"], ["SN-001-2024-A"], ["SN-002-2024-B"], ["SN-003-2024-C"]];
    } else {
      data = [
        ["Item", "Serial"],
        ["Cash Drawer CD-100", "SN-001-2024-A"],
        ["Cash Drawer CD-100", "SN-002-2024-B"],
        ["POS Terminal PT-200", "SN-003-2024-C"],
      ];
    }

    data.forEach((row) => {
      worksheet.addRow(row);
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName =
      mode === "serial-only"
        ? "3nStar Import Serials - Serial Only.xlsx"
        : "3nStar Import Serials - Item and Serial.xlsx";
    saveAs(blob, fileName);
  };

  const handleFileUpload = (file: RcFile): boolean => {
    const isExcel =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (!isExcel) {
      toastText("You can only upload Excel file!", "error");
      return false;
    }

    const uploadFile: UploadFile = {
      uid: `${Date.now()}-${file.name}`,
      name: file.name,
      status: "done",
      originFileObj: file,
      size: file.size,
      type: file.type,
    };
    setFileList([uploadFile]);
    return false;
  };

  const validateAndPreview = async () => {
    if (fileList.length === 0) {
      toastText("Please upload an Excel file", "error");
      return;
    }

    if (uploadMode === "serial-only" && !selectedItem) {
      toastText("Please select an item for serial-only import", "error");
      return;
    }

    setIsValidating(true);

    try {
      const reader = new FileReader();
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const binaryStr = e.target?.result;
        if (!binaryStr) {
          setIsValidating(false);
          return;
        }
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(sheet);

        if (jsonData.length === 0) {
          toastText("No data rows found", "error");
          setIsValidating(false);
          return;
        }

        // Derive column names from the sheet header row, not from the first data row.
        // When the first data row has an empty cell (e.g. empty Serial), the row object
        // may omit that key, so using jsonData[0] would wrongly report "Serial" as missing.
        const headerRow = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
        })[0] as (string | undefined)[] | undefined;
        const fileColumns = (headerRow || []).map((h) =>
          String(h ?? "").trim().toLowerCase()
        ).filter(Boolean);

        const hasSerialColumn = fileColumns.some((col) => col === "serial");
        if (uploadMode === "serial-only") {
          if (!hasSerialColumn) {
            toastText("Required column(s) missing. Expected: Serial.", "error");
            setIsValidating(false);
            return;
          }
          const hasItemColumn = fileColumns.some((col) => col === "item");
          if (hasItemColumn) {
            toastText(
              "Item column conflict: You selected Serial Only mode but the file contains an Item column. Please use Item + Serial mode or upload a file with Serial column only.",
              "error"
            );
            setIsValidating(false);
            return;
          }
        } else {
          const hasItemColumn = fileColumns.some((col) => col === "item");
          if (!hasSerialColumn || !hasItemColumn) {
            const missing = [
              !hasSerialColumn && "Serial",
              !hasItemColumn && "Item",
            ].filter(Boolean).join(", ");
            toastText(`Required column(s) in the item and serial mode are missing. Expected: ${missing}.`, "error");
            setIsValidating(false);
            return;
          }
        }

        const previewRows: PreviewRow[] = [];
        // Map to store first occurrence row number for each serial (case-insensitive)
        const serialFirstOccurrence = new Map<string, number>();

        // Create a set of master item names for quick lookup (case-insensitive)
        const masterItemNames = new Set(
          items.map((item) => item.value.trim().toLowerCase())
        );

        jsonData.forEach((row: Record<string, string>, index: number) => {
          const rowNumber = index + 1;
          let item = "";
          let serial = "";

          if (uploadMode === "serial-only") {
            serial = row.Serial || row.serial || "";
            item = selectedItem || "";
          } else {
            item = row.Item || row.item || "";
            serial = row.Serial || row.serial || "";
          }

          let status: "valid" | "invalid" | "duplicate" = "valid";
          let error = "";
          let errorColumn = "";

          // Check for empty serial first
          if (!serial || serial.trim() === "") {
            status = "invalid";
            error = "Serial is empty";
            errorColumn = "Serial";
          } else if (serial.trim().length > 20) {
            status = "invalid";
            error = "Serial exceeds 20 characters";
            errorColumn = "Serial";
          } else {
            const serialKey = serial.trim().toLowerCase();
            if (serialFirstOccurrence.has(serialKey)) {
              const firstOccurrenceRow = serialFirstOccurrence.get(serialKey);
              status = "duplicate";
              error = `Duplicate of row ${firstOccurrenceRow}`;
              errorColumn = "Serial";
            } else {
              serialFirstOccurrence.set(serialKey, rowNumber);
            }

            if (status !== "duplicate" && uploadMode === "item-serial") {
              if (!item || item.trim() === "") {
                status = "invalid";
                error = "Item is empty";
                errorColumn = "Item";
              } else {
                const itemKey = item.trim().toLowerCase();
                if (!masterItemNames.has(itemKey)) {
                  status = "invalid";
                  error = "No item was found in master";
                  errorColumn = "Item";
                }
              }
            }
          }

          previewRows.push({
            rowNumber,
            item: item || "-",
            serial: serial || "-",
            status,
            error: error || "-",
            errorColumn: errorColumn || undefined,
          });
        });

        // Backend checks so preview shows same errors as error report Excel
        const invoiceNumber = (formValues.invoiceNumber as string)?.trim?.();
        if (invoiceNumber) {
          try {
            const checkInvoiceRes = await postApi(
              "/productWarrantyDetails/check-invoice-used",
              { invoiceNumber }
            );
            const invoiceUsed =
              checkInvoiceRes.data?.data?.used === true ||
              checkInvoiceRes.data?.used === true;
            if (invoiceUsed) {
              previewRows.forEach((row) => {
                row.status = "invalid";
                row.error = "Duplicate invoice not allowed.";
                row.errorColumn = "General";
              });
              setPreviewData([...previewRows]);
              setPreviewPagination((prev) => ({ current: 1, pageSize: prev.pageSize }));
              setValidationSummary({
                total: previewRows.length,
                valid: 0,
                invalid: previewRows.length,
                duplicates: 0,
              });
              setCurrentStep(2);
              setIsValidating(false);
              return;
            }
          } catch (err) {
            // If check fails, continue with preview without marking duplicate invoice
          }

          const validRows = previewRows.filter((r) => r.status === "valid");
          const validSerials = validRows.map((r) =>
            (r.serial === "-" ? "" : r.serial).trim()
          );
          if (validSerials.length > 0) {
            try {
              const checkSerialsRes = await postApi(
                "/productWarrantyDetails/check-serials-complete",
                { invoiceNumber, serials: validSerials }
              );
              const completeSerials: string[] =
                checkSerialsRes.data?.data?.completeSerials ??
                checkSerialsRes.data?.completeSerials ??
                [];
              const completeSet = new Set(
                completeSerials.map((s: string) => (s ?? "").trim().toLowerCase())
              );
              previewRows.forEach((row) => {
                if (row.status === "valid") {
                  const serialNorm = (row.serial === "-" ? "" : row.serial)
                    .trim()
                    .toLowerCase();
                  if (completeSet.has(serialNorm)) {
                    row.status = "invalid";
                    row.error = "Serial already exists and is complete.";
                    row.errorColumn = "Serial";
                  }
                }
              });
            } catch (err) {
              // If check fails, continue with preview without marking complete serials
            }
          }

          // Serials already registered with a different invoice number (same error as in Excel)
          const validRowsAfterComplete = previewRows.filter((r) => r.status === "valid");
          const serialsForOtherInvoice = validRowsAfterComplete.map((r) =>
            (r.serial === "-" ? "" : r.serial).trim()
          );
          if (serialsForOtherInvoice.length > 0) {
            try {
              const otherInvoiceRes = await postApi(
                "/productWarrantyDetails/check-serials-other-invoice",
                { invoiceNumber, serials: serialsForOtherInvoice }
              );
              const serialsWithOtherInvoice: string[] =
                otherInvoiceRes.data?.data?.serialsWithOtherInvoice ??
                otherInvoiceRes.data?.serialsWithOtherInvoice ??
                [];
              const otherInvoiceSet = new Set(
                serialsWithOtherInvoice.map((s: string) => (s ?? "").trim().toLowerCase())
              );
              previewRows.forEach((row) => {
                if (row.status === "valid") {
                  const serialNorm = (row.serial === "-" ? "" : row.serial)
                    .trim()
                    .toLowerCase();
                  if (otherInvoiceSet.has(serialNorm)) {
                    row.status = "invalid";
                    row.error =
                      "Serial number is already registered with a different invoice number.";
                    row.errorColumn = "Serial";
                  }
                }
              });
            } catch (err) {
              // If check fails, continue with preview without marking
            }
          }
        }

        setPreviewData([...previewRows]);
        setPreviewPagination((prev) => ({ current: 1, pageSize: prev.pageSize }));

        const summary = {
          total: previewRows.length,
          valid: previewRows.filter((r) => r.status === "valid").length,
          invalid: previewRows.filter((r) => r.status === "invalid").length,
          duplicates: previewRows.filter((r) => r.status === "duplicate").length,
        };

        setValidationSummary(summary);
        setCurrentStep(2);
        setIsValidating(false);
      };

      if (fileList[0]?.originFileObj) {
        reader.readAsArrayBuffer(fileList[0].originFileObj);
      }
    } catch (error) {
      setIsValidating(false);
      toastText("Error reading file", "error");
      console.error("Error:", error);
    }
  };

  const handleConfirmImport = async () => {

    setIsLoading(true);
    setIsImporting(true);

    try {
      const invoiceDate = formValues.invoiceDate
        ? dayjs(formValues.invoiceDate).format("YYYY-MM-DD")
        : null;

      if (!invoiceDate || !formValues.customerName || !formValues.invoiceNumber) {
        toastText("Please complete all invoice details", "error");
        setIsLoading(false);
        setIsImporting(false);
        return;
      }

      const validRows = previewData.filter((row) => row.status === "valid");
      const failedRows = previewData.filter(
        (row) => row.status === "invalid" || row.status === "duplicate"
      );
      const failedRowsErrorData = failedRows.map((row) => ({
        rowNumber: row.rowNumber,
        serial: row.serial === "-" ? "" : row.serial,
        error: row.error === "-" ? "" : row.error,
        column: row.errorColumn ?? "General",
      }));

      const invoiceNumber = (formValues.invoiceNumber as string)?.trim() ?? "";

      const checkInvoiceRes = await postApi(
        "/productWarrantyDetails/check-invoice-used",
        { invoiceNumber }
      );
      const invoiceUsed =
        checkInvoiceRes.data?.data?.used === true ||
        checkInvoiceRes.data?.used === true;
      if (invoiceUsed) {
        const duplicateInvoiceErrors = validRows.map((row) => ({
          rowNumber: row.rowNumber,
          serial: row.serial === "-" ? "" : row.serial,
          error: "Duplicate invoice not allowed.",
          column: "General",
        }));
        const allErrorData = [...failedRowsErrorData, ...duplicateInvoiceErrors];
        setImportResults({
          successful: 0,
          failed: allErrorData.length,
          errorData: allErrorData,
        });
        dispatch(
          getWarrantyDetailsActionTable({
            pageNumber: currentPage,
            pageSize: pageSize,
            searchParams: searchParams,
          })
        );
        setCurrentStep(3);
        setIsLoading(false);
        setIsImporting(false);
        return;
      }

      const checkSerialsRes = await postApi(
        "/productWarrantyDetails/check-serials-complete",
        {
          invoiceNumber,
          serials: validRows.map((r) => (r.serial === "-" ? "" : r.serial).trim()),
        }
      );
      const completeSerials: string[] =
        checkSerialsRes.data?.data?.completeSerials ??
        checkSerialsRes.data?.completeSerials ??
        [];
      const serialNorm = (s: string) => (s ?? "").trim().toLowerCase();
      const completeSet = new Set(completeSerials.map(serialNorm));

      const completeErrorData = validRows
        .filter((row) => completeSet.has(serialNorm(row.serial === "-" ? "" : row.serial)))
        .map((row) => ({
          rowNumber: row.rowNumber,
          serial: row.serial === "-" ? "" : row.serial,
          error: "Serial already exists and is complete.",
          column: "Serial",
        }));

      const itemsToUpload = validRows.filter(
        (row) => !completeSet.has(serialNorm(row.serial === "-" ? "" : row.serial))
      );

      if (itemsToUpload.length === 0) {
        const allErrorData = [...failedRowsErrorData, ...completeErrorData];
        setImportResults({
          successful: 0,
          failed: allErrorData.length,
          errorData: allErrorData,
        });
        dispatch(
          getWarrantyDetailsActionTable({
            pageNumber: currentPage,
            pageSize: pageSize,
            searchParams: searchParams,
          })
        );
        setCurrentStep(3);
        setIsLoading(false);
        setIsImporting(false);
        return;
      }

      // Call upload-invoice only for rows that are not "already complete"
      const requestData = {
        customerName: formValues.customerName as string,
        invoiceNumber: formValues.invoiceNumber as string,
        invoiceDate,
        items: itemsToUpload.map((row) => ({
          item: row.item,
          serial: row.serial,
        })),
      };

      const response = await postApi(
        "/productWarrantyDetails/upload-invoice",
        requestData
      );

      if (response.status === 201) {
        const apiData = response.data?.data ?? response.data;
        const successfulCount = apiData?.created ?? 0;
        const apiInvalidData: Array<{
          itemName?: string;
          serialNumber?: string;
          error?: string;
        }> = apiData?.invalidData ?? [];

        const apiFailedRows = apiInvalidData
          .filter((data) => data.error)
          .map((data, index) => {
            const serialNormIn = (data.serialNumber ?? "").trim().toLowerCase();
            const itemNormIn = (data.itemName ?? "").trim().toLowerCase();
            const previewRow = previewData.find(
              (row) =>
                serialNorm((row.serial === "-" ? "" : row.serial)) === serialNormIn &&
                (row.item === "-" ? "" : row.item).trim().toLowerCase() === itemNormIn
            );
            return {
              rowNumber: previewRow?.rowNumber ?? index + 1,
              serial: (data.serialNumber ?? "").trim(),
              error: data.error ?? "",
              column: "Serial" as const,
            };
          });

        const allErrorData = [
          ...failedRowsErrorData,
          ...completeErrorData,
          ...apiFailedRows,
        ];
        const uniqueErrorData = Array.from(
          new Map(
            allErrorData.map((item) => [
              `${item.rowNumber}-${item.serial}`,
              { ...item, column: item.column ?? "General" },
            ])
          ).values()
        );

        setImportResults({
          successful: successfulCount,
          failed: uniqueErrorData.length,
          errorData: uniqueErrorData,
        });

        dispatch(
          getWarrantyDetailsActionTable({
            pageNumber: currentPage,
            pageSize: pageSize,
            searchParams: searchParams,
          })
        );
        setCurrentStep(3);
      } else {
        toastText("Something went wrong in uploading invoice", "error");
      }
    } catch (error) {
      console.error("Error:", error);
      toastText("Error uploading invoice data", "error");
    } finally {
      setIsLoading(false);
      setIsImporting(false);
    }
  };

  const generateAndDownloadExcel = async (
    invalidData: Array<{ itemName?: string; serialNumber?: string; error?: string }>,
    fileName: string
  ) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Errors");

    const headers = ["Item", "Serial Number", "Error"];
    worksheet.addRow(headers);

    invalidData.forEach((row) => {
      const errorColumnValue = row.error || "";
      const rowData = [row.itemName || "", row.serialNumber || "", errorColumnValue];

      const rowAdded = worksheet.addRow(rowData);

      if (errorColumnValue) {
        rowAdded.eachCell((cell) => {
          cell.style = {
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFF00" },
            },
          };
        });

        const lastCell = rowAdded.getCell(headers.length);
        lastCell.style.font = {
          color: { argb: "FF0000" },
          bold: true,
        };
      }
    });

    worksheet.columns.forEach((column) => {
      column.width = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.xlsx`;
    link.click();
  };

  const generateErrorReport = async () => {
    if (importResults.errorData.length === 0) {
      toastText("No errors to download", "info");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Errors");

    const headers = ["Row Number", "Column", "Serial", "Error"];
    const headerRow = worksheet.addRow(headers);
    
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
    });

    importResults.errorData.forEach((row) => {
      const rowData = [row.rowNumber, row.column ?? "-", row.serial, row.error];
      worksheet.addRow(rowData);
    });

    worksheet.columns = [
      { width: 15 }, // Row Num
      { width: 12 }, // Column
      { width: 25 }, // Serial
      { width: 40 }, // Error
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `error_report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`;
    link.click();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setFileList([]);
    setCurrentStep(0);
    form.resetFields();
    setFormValues({});
    setUploadMode("serial-only");
    setSelectedItem(null);
    setPreviewData([]);
    setPreviewPagination({ current: 1, pageSize: 10 });
    setValidationSummary({ total: 0, valid: 0, invalid: 0, duplicates: 0 });
    setImportResults({ successful: 0, failed: 0, errorData: [] });
    setIsImporting(false);
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields(["customerName", "invoiceNumber", "invoiceDate"]);
        // Store form values before moving to next step
        const values = form.getFieldsValue();
        setFormValues(values);
        setCurrentStep(1);
      } catch (error) {
        console.error("Validation failed:", error);
      }
    } else if (currentStep === 1) {
      await validateAndPreview();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      // If going back to step 0, restore form values
      if (currentStep === 1) {
        form.setFieldsValue(formValues);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadProps = {
    beforeUpload: handleFileUpload,
    onRemove: () => {
      setFileList([]);
    },
    fileList: fileList,
    accept: ".xlsx",
  };

  const previewColumns = [
    {
      title: "ROW #",
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 80,
    },
    {
      title: "ITEM",
      dataIndex: "item",
      key: "item",
    },
    {
      title: "SERIAL",
      dataIndex: "serial",
      key: "serial",
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; text: string }> = {
          valid: { color: "success", text: "Valid" },
          invalid: { color: "error", text: "Invalid" },
          duplicate: { color: "warning", text: "Duplicate" },
        };
        const config = statusConfig[status] || statusConfig.valid;
        return (
          <span
            style={{
              padding: "4px 8px",
              borderRadius: "4px",
              backgroundColor:
                config.color === "success"
                  ? "#f6ffed"
                  : config.color === "error"
                  ? "#fff2f0"
                  : "#fffbe6",
              color:
                config.color === "success"
                  ? "#52c41a"
                  : config.color === "error"
                  ? "#ff4d4f"
                  : "#faad14",
              fontSize: "12px",
            }}
          >
            {config.text}
          </span>
        );
      },
    },
    {
      title: "ERROR",
      dataIndex: "error",
      key: "error",
      render: (error: string) => (
        <span style={{ color: error !== "-" ? "#ff4d4f" : "#000" }}>
          {error}
        </span>
      ),
    },
  ];

  const steps = [
    {
      title: "Invoice Details",
    },
    {
      title: "Upload File",
    },
    {
      title: "Preview & Confirm",
    },
    {
      title: "Import Results",
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Customer Name"
                  name="customerName"
                  rules={[{ required: true, message: "Please select customer name" }]}
                >
                  <Select
                    placeholder="Select Customer Name"
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input: string, option?: { label?: string }) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    suffixIcon={<SearchOutlined />}
                    options={customers}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Invoice Number"
                  name="invoiceNumber"
                  rules={[
                    { required: true, message: "Please enter invoice number" },
                    {
                      pattern: /^[a-zA-Z0-9]([a-zA-Z0-9\s\-:]*[a-zA-Z0-9])?$/,
                      message: "Invalid Invoice Number",
                    },
                  ]}
                >
                  <Input placeholder="Enter Invoice Number" size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Invoice Date"
                  name="invoiceDate"
                  rules={[{ required: true, message: "Please select invoice date" }]}
                  style={{ marginTop: 16 }}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    size="large"
                    format="MM/DD/YYYY"
                    suffixIcon={<CalendarOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        );

      case 1:
        return (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ fontSize: 14 }}>
                Upload Mode
              </Text>
              <Tooltip
                title={
                  <div>
                    <div>Serial-only: When all serials in your file belong to a single item type.</div>
                    <div>Item + Serial: When your file contains multiple items, each with their own serials.</div>
                  </div>
                }
                placement="top"
              >
                <InfoCircleOutlined
                  style={{ marginLeft: 8, color: "#1890ff", cursor: "help" }}
                />
              </Tooltip>
              <Radio.Group
                value={uploadMode}
                onChange={(e) => {
                  setUploadMode(e.target.value);
                  // Don't reset selectedItem when switching modes - preserve it for serial-only mode
                }}
                style={{ marginTop: 16, width: "100%" }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <div
                      onClick={() => setUploadMode("serial-only")}
                      style={{
                        padding: "12px",
                        border:
                          uploadMode === "serial-only"
                            ? "2px solid #1890ff"
                            : "1px solid #d9d9d9",
                        borderRadius: "4px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    >
                      <Radio value="serial-only">
                        <span style={{ fontWeight: 500 }}>Serial-only (Single Item)</span>
                      </Radio>
                      <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4, marginLeft: 22 }}>
                        All serials belong to one item
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div
                      onClick={() => setUploadMode("item-serial")}
                      style={{
                        padding: "12px",
                        border:
                          uploadMode === "item-serial"
                            ? "2px solid #1890ff"
                            : "1px solid #d9d9d9",
                        borderRadius: "4px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    >
                      <Radio value="item-serial">
                        <span style={{ fontWeight: 500 }}>Item + Serial (Full Invoice)</span>
                      </Radio>
                      <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4, marginLeft: 22 }}>
                        Invoice has multiple items
                      </div>
                    </div>
                  </Col>
                </Row>
              </Radio.Group>

              {uploadMode === "serial-only" && (
                <div style={{ marginTop: 16 }}>
                  <Text strong style={{ marginRight: 2 }}>Item</Text>
                  <span style={{ color: "red" }}>*</span>
                  <Select
                    style={{ width: "100%", marginTop: 8 }}
                    placeholder="Select Item"
                    value={selectedItem}
                    onChange={setSelectedItem}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input: string, option?: { label?: string }) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={items}
                    size="large"
                  />
                  <Alert
                    message="Excel must contain only 'Serial' column."
                    type="info"
                    icon={<InfoCircleOutlined />}
                    style={{ marginTop: 12 }}
                  />
                </div>
              )}

              {uploadMode === "item-serial" && (
                <Alert
                  message="Excel must contain 'Item' and 'Serial' columns."
                  type="info"
                  icon={<InfoCircleOutlined />}
                  style={{ marginTop: 16 }}
                />
              )}
            </div>

            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Text strong style={{ fontSize: 14 }}>
                  Upload Excel File
                </Text>
                <Button
                  type="link"
                  icon={<FileExcelOutlined />}
                  onClick={() => downloadSampleFile(uploadMode)}
                >
                  Download Sample File
                </Button>
              </div>

              {fileList.length === 0 ? (
                <Upload.Dragger {...uploadProps} showUploadList={false}>
                  <p>
                    <UploadOutlined className="ant-upload-drag-icon" />
                  </p>
                  <p className="ant-upload-text">
                    Click to upload or drag and drop
                  </p>
                  <p className="ant-upload-hint">
                    Only .xlsx files are accepted
                  </p>
                </Upload.Dragger>
              ) : (
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#f0f7ff",
                    borderRadius: "4px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <FileExcelOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{fileList[0]?.name || ""}</div>
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                        {fileList[0]?.size ? (fileList[0].size / 1024).toFixed(1) : "0"} KB
                      </div>
                    </div>
                  </div>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => setFileList([])}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Space size={16} style={{ width: "100%", marginBottom: 24 }} wrap>
              <Card style={{ flex: 1, minWidth: 175 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <InfoCircleOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                    <div style={{ fontSize: 24, fontWeight: "bold" }}>
                      {validationSummary.total}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginLeft: 28 }}>Total Rows</div>
                </div>
              </Card>
              <Card style={{ flex: 1, minWidth: 175 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <CheckCircleOutlined style={{ fontSize: 20, color: "#52c41a" }} />
                    <div style={{ fontSize: 24, fontWeight: "bold", color: "#52c41a" }}>
                      {validationSummary.valid}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginLeft: 28 }}>Valid</div>
                </div>
              </Card>
              <Card style={{ flex: 1, minWidth: 175 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <CloseCircleOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />
                    <div style={{ fontSize: 24, fontWeight: "bold", color: "#ff4d4f" }}>
                      {validationSummary.invalid}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginLeft: 28 }}>Invalid</div>
                </div>
              </Card>
              <Card style={{ flex: 1, minWidth: 175 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <WarningOutlined style={{ fontSize: 20, color: "#faad14" }} />
                    <div style={{ fontSize: 24, fontWeight: "bold", color: "#faad14" }}>
                      {validationSummary.duplicates}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginLeft: 28 }}>Duplicates</div>
                </div>
              </Card>
            </Space>

            <Table
              columns={previewColumns}
              dataSource={previewData}
              rowKey="rowNumber"
              pagination={{
                current: previewPagination.current,
                pageSize: previewPagination.pageSize,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} rows`,
                onChange: (page, pageSize) => {
                  setPreviewPagination((prev) => ({
                    current: page,
                    pageSize: pageSize ?? prev.pageSize,
                  }));
                },
              }}
              scroll={{ y: 300 }}
              style={{ marginBottom: 16 }}
            />

            {validationSummary.valid > 0 &&
              validationSummary.invalid === 0 &&
              validationSummary.duplicates === 0 && (
                <Alert
                  message="No errors in the rows. All rows are valid and ready to import."
                  type="success"
                  icon={<CheckCircleOutlined />}
                  showIcon
                />
              )}
            {validationSummary.valid > 0 &&
              (validationSummary.invalid > 0 || validationSummary.duplicates > 0) && (
                <Alert
                  message={`Only ${validationSummary.valid} valid row${
                    validationSummary.valid > 1 ? "s" : ""
                  } will be imported. Invalid and duplicate rows will be skipped.`}
                  type="warning"
                  icon={<WarningOutlined />}
                  showIcon
                />
              )}
            {validationSummary.valid === 0 && (
              <Alert
                message="No valid rows found. Please check your file and try again."
                type="error"
                icon={<CloseCircleOutlined />}
                showIcon
              />
            )}
          </div>
        );

      case 3: {
        const hasErrors = importResults.failed > 0;
        return (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ marginBottom: 32 }}>
              {hasErrors ? (
                <CloseCircleOutlined
                  style={{
                    fontSize: 64,
                    color: "#ff4d4f",
                    marginBottom: 16,
                  }}
                />
              ) : (
                <CheckCircleOutlined
                  style={{
                    fontSize: 64,
                    color: "#52c41a",
                    marginBottom: 16,
                  }}
                />
              )}
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                {hasErrors
                  ? "Import Completed with Errors"
                  : "Import Completed Successfully"}
              </div>
              <div style={{ fontSize: 14, color: "#8c8c8c" }}>
                Your serial numbers have been processed.
              </div>
            </div>

            <Row gutter={16} style={{ marginBottom: 32 }}>
              <Col span={12}>
                <Card
                  style={{
                    backgroundColor: "#f6ffed",
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 36,
                        fontWeight: "bold",
                        color: "#52c41a",
                        marginBottom: 8,
                      }}
                    >
                      {importResults.successful}
                    </div>
                    <div style={{ fontSize: 14, color: "#8c8c8c" }}>
                      Successfully Imported
                    </div>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  style={{
                    backgroundColor: "#fff2f0",
                    border: "1px solid #ffccc7",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 36,
                        fontWeight: "bold",
                        color: "#ff4d4f",
                        marginBottom: 8,
                      }}
                    >
                      {importResults.failed}
                    </div>
                    <div style={{ fontSize: 14, color: "#8c8c8c" }}>
                      Failed / Skipped
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            {hasErrors && (
              <div style={{ marginBottom: 24 }}>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={generateErrorReport}
                  size="large"
                  style={{
                    borderColor: "#1890ff",
                    color: "#1890ff",
                  }}
                >
                  Download Error Report
                </Button>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            Import Serials from Excel
          </div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
            One invoice per import. Enter invoice details first, then upload Excel.
          </div>
        </div>
      }
      open={isModalVisible}
      onCancel={handleCancel}
      width={800}
      footer={null}
      closable
      centered
      maskClosable={false}
    >
      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 32 }}
      />

      <div style={{ minHeight: 400, marginBottom: 24 }}>
        {renderStepContent()}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          {currentStep > 0 && currentStep < 3 && (
            <Button onClick={handleBack} size="large">
              Back
            </Button>
          )}
        </div>
        <Space>
          {currentStep === 3 ? (
            <Button type="primary" onClick={handleCancel} size="large">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>
              {currentStep < 2 ? (
                <Button
                  type="primary"
                  onClick={handleNext}
                  size="large"
                  loading={isValidating}
                  disabled={
                    (currentStep === 0 && !isStep0Complete) ||
                    (currentStep === 1 && (fileList.length === 0 || (uploadMode === "serial-only" && !selectedItem)))
                  }
                >
                  {currentStep === 1 ? "Validate & Preview" : "Next"}
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleConfirmImport}
                  size="large"
                  loading={isImporting}
                >
                  Confirm Import
                </Button>
              )}
            </>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default UploadInvoiceModal;
